import { getFunctionAuthHeaders } from 'utils/getFunctionsBaseUrl';
import { z } from 'zod';
import { post } from './index';

// Contract Parsing Types and Schema
const ContractExtractedDataSchema = z.object({
    guests: z.string().nullable(),
    guestroomNights: z.string().nullable(),
    guestroomCost: z.string().nullable(),
    guestroomNightlyRate: z.string().nullable(),
    roomCost: z.string().nullable(),
    cateringCost: z.string().nullable(),
    estimateTotal: z.string().nullable(),
});

const ContractParsingResponseSchema = z.object({
    success: z.boolean(),
    extractedData: ContractExtractedDataSchema.optional(),
    error: z.string().optional(),
});

export type ContractExtractedData = z.infer<typeof ContractExtractedDataSchema>;
export type ContractParsingResponse = z.infer<typeof ContractParsingResponseSchema>;

export const generateInviteSubjectAndBody = (
    meetingName: string,
    meetingDescription: string,
    location: string,
    start: string,
    end: string,
    fromName: string,
    fromCompanyName: string
) =>
    post(`ai/generate-invite`, {
        meetingName,
        meetingDescription,
        location,
        start,
        end,
        fromName,
        fromCompanyName,
    });

/**
 * Parse contract text to extract spend estimate data (for DOCX)
 * @param documentText - The extracted text content from the contract document
 * @returns Parsed contract data with extracted financial fields
 */
export const parseContractText = async (documentText: string): Promise<ContractParsingResponse> => {
    try {
        const response = await fetch('/.netlify/functions/parse-contract', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getFunctionAuthHeaders(),
            },
            body: JSON.stringify({ documentText }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        const validationResult = ContractParsingResponseSchema.safeParse(data);

        if (!validationResult.success) {
            console.error('Invalid contract parsing response:', data);
            console.error('Validation errors:', validationResult.error.format());
            return {
                success: false,
                error: 'Invalid response structure from contract parsing API',
            };
        }

        return validationResult.data;
    } catch (error) {
        console.error('Contract parsing failed:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        return {
            success: false,
            error: `Failed to parse contract: ${errorMessage}`,
        };
    }
};

/**
 * Parse contract images using Vision API (for PDF)
 * Images are rendered in the browser and sent to the API for OpenAI Vision analysis
 * @param images - Array of base64 data URLs of contract page images
 * @returns Parsed contract data with extracted financial fields
 */
export const parseContractImages = async (images: string[]): Promise<ContractParsingResponse> => {
    try {
        const response = await fetch('/.netlify/functions/parse-contract', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getFunctionAuthHeaders(),
            },
            body: JSON.stringify({ images }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        const validationResult = ContractParsingResponseSchema.safeParse(data);

        if (!validationResult.success) {
            console.error('Invalid contract parsing response:', data);
            console.error('Validation errors:', validationResult.error.format());
            return {
                success: false,
                error: 'Invalid response structure from contract parsing API',
            };
        }

        return validationResult.data;
    } catch (error) {
        console.error('Contract parsing failed:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        return {
            success: false,
            error: `Failed to parse contract: ${errorMessage}`,
        };
    }
};

/**
 * Parse legacy .doc contract file (server-side extraction)
 * The file is sent as base64 to the server where word-extractor extracts the text
 * @param file - The .doc file to parse
 * @returns Parsed contract data with extracted financial fields
 */
export const parseContractDoc = async (file: File): Promise<ContractParsingResponse> => {
    try {
        // Convert file to base64
        const arrayBuffer = await file.arrayBuffer();
        const base64 = btoa(new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''));

        const response = await fetch('/.netlify/functions/parse-contract', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getFunctionAuthHeaders(),
            },
            body: JSON.stringify({ docFileBase64: base64 }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        const validationResult = ContractParsingResponseSchema.safeParse(data);

        if (!validationResult.success) {
            console.error('Invalid contract parsing response:', data);
            console.error('Validation errors:', validationResult.error.format());
            return {
                success: false,
                error: 'Invalid response structure from contract parsing API',
            };
        }

        return validationResult.data;
    } catch (error) {
        console.error('Contract parsing failed:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        return {
            success: false,
            error: `Failed to parse contract: ${errorMessage}`,
        };
    }
};
